import React, { useState } from 'react';

const CATEGORY_META = {
  beach: { icon: '\u2600\ufe0f', label: 'Beach', color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' },
  mountain: { icon: '\u26f0\ufe0f', label: 'Mountain', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  city: { icon: '\ud83c\udfd9\ufe0f', label: 'City', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  cultural: { icon: '\ud83c\udfdb\ufe0f', label: 'Cultural', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  adventure: { icon: '\ud83e\uddf3', label: 'Adventure', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  romantic: { icon: '\u2764\ufe0f', label: 'Romantic', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
  culinary: { icon: '\ud83c\udf7d\ufe0f', label: 'Culinary', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
  historical: { icon: '\ud83c\udff0', label: 'Historical', color: '#a78bfa', gradient: 'linear-gradient(135deg, #a78bfa, #8b5cf6)' },
  wellness: { icon: '\ud83e\uddd8', label: 'Wellness', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
  nightlife: { icon: '\ud83c\udf1f', label: 'Nightlife', color: '#a855f7', gradient: 'linear-gradient(135deg, #a855f7, #9333ea)' },
};

const TripComparisonCard = ({ tripPlan, onSelectTrip }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!tripPlan || !tripPlan.is_diverse_comparison || !tripPlan.destination_options?.length) {
    return null;
  }

  const { destination_options, recommended, region, categories_searched } = tripPlan;

  const toggleExpand = (idx) => {
    setExpandedIdx(expandedIdx === idx ? null : idx);
  };

  return (
    <div data-testid="trip-comparison-card" style={styles.wrapper}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.headerTitle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d4a054" strokeWidth="2" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          </svg>
          Trip Comparison
        </h3>
        <div style={styles.headerMeta}>
          {region && <span style={styles.regionBadge}>{region.charAt(0).toUpperCase() + region.slice(1)}</span>}
          <span style={styles.countBadge}>{destination_options.length} options</span>
        </div>
      </div>

      {/* Category pills */}
      {categories_searched?.length > 0 && (
        <div style={styles.categoryPills}>
          {categories_searched.map((cat) => {
            const meta = CATEGORY_META[cat] || { icon: '\ud83c\udf0d', label: cat, color: '#64748b' };
            return (
              <span key={cat} style={{ ...styles.pill, borderColor: meta.color, color: meta.color }}>
                {meta.icon} {meta.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Option cards */}
      <div style={styles.grid}>
        {destination_options.map((opt, idx) => {
          const meta = CATEGORY_META[opt.category] || CATEGORY_META.city;
          const isRecommended = opt.city === recommended;
          const isExpanded = expandedIdx === idx;

          return (
            <div
              key={`${opt.city}-${idx}`}
              data-testid={`trip-option-${opt.city.toLowerCase().replace(/\s/g, '-')}`}
              style={{
                ...styles.optionCard,
                borderColor: isRecommended ? '#d4a054' : 'rgba(255,255,255,0.08)',
                boxShadow: isRecommended ? '0 0 20px rgba(212,160,84,0.15)' : 'none',
              }}
            >
              {/* Recommended badge */}
              {isRecommended && (
                <div data-testid="recommended-badge" style={styles.recommendedBadge}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#d4a054" stroke="none">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  Best Value
                </div>
              )}

              {/* Category tag */}
              <div style={{ ...styles.categoryTag, background: meta.gradient }}>
                {meta.icon} {meta.label}
              </div>

              {/* City & country */}
              <h4 style={styles.cityName}>{opt.city}</h4>
              <p style={styles.countryName}>{opt.country}</p>

              {/* Vibe */}
              <p style={styles.vibe}>{opt.vibe}</p>

              {/* Price */}
              <div style={styles.priceRow}>
                <span style={styles.priceLabel}>Est. Total</span>
                <span style={styles.priceValue}>
                  ${opt.estimated_total ? opt.estimated_total.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '---'}
                </span>
              </div>

              {/* Expand toggle */}
              <button
                data-testid={`expand-option-${idx}`}
                style={styles.expandBtn}
                onClick={() => toggleExpand(idx)}
              >
                {isExpanded ? 'Less Details' : 'More Details'}
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ marginLeft: '4px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div style={styles.details}>
                  {opt.flight && (
                    <div style={styles.detailSection}>
                      <span style={styles.detailIcon}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                      </span>
                      <div>
                        <p style={styles.detailKey}>Flight</p>
                        <p style={styles.detailVal}>{opt.flight.airline} &mdash; ${opt.flight.price}{opt.flight.stops > 0 ? ` (${opt.flight.stops} stop${opt.flight.stops > 1 ? 's' : ''})` : ' (direct)'}</p>
                      </div>
                    </div>
                  )}
                  {opt.hotel && (
                    <div style={styles.detailSection}>
                      <span style={styles.detailIcon}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      </span>
                      <div>
                        <p style={styles.detailKey}>Hotel</p>
                        <p style={styles.detailVal}>{opt.hotel.name} &mdash; ${opt.hotel.price_per_night}/night ({opt.hotel.rating} stars)</p>
                      </div>
                    </div>
                  )}
                  {opt.cost_breakdown && (
                    <div style={styles.breakdownBox}>
                      <div style={styles.breakdownRow}><span>Flights</span><span>${opt.cost_breakdown.flights?.toLocaleString()}</span></div>
                      <div style={styles.breakdownRow}><span>Hotels</span><span>${opt.cost_breakdown.accommodation?.toLocaleString()}</span></div>
                      <div style={styles.breakdownRow}><span>Extras</span><span>${opt.cost_breakdown.daily_extras?.toLocaleString()}</span></div>
                    </div>
                  )}
                </div>
              )}

              {/* Plan this trip CTA */}
              <button
                data-testid={`plan-trip-${opt.city.toLowerCase().replace(/\s/g, '-')}`}
                style={{ ...styles.planBtn, background: isRecommended ? 'linear-gradient(135deg, #d4a054, #c4903c)' : 'rgba(255,255,255,0.08)' }}
                onClick={() => onSelectTrip && onSelectTrip(opt)}
              >
                Plan This Trip
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    margin: '1rem 0',
    borderRadius: '0.75rem',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  headerTitle: {
    color: '#f1f5f9',
    fontSize: '1rem',
    fontWeight: 700,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  headerMeta: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  regionBadge: {
    background: 'rgba(212,160,84,0.15)',
    color: '#d4a054',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  countBadge: {
    background: 'rgba(255,255,255,0.06)',
    color: '#94a3b8',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
  },
  categoryPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem',
  },
  pill: {
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    border: '1px solid',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem',
    padding: '0.75rem 1.25rem 1.25rem',
  },
  optionCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '0.75rem',
    padding: '1.25rem 1rem',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  recommendedBadge: {
    position: 'absolute',
    top: '-1px',
    right: '1rem',
    background: 'rgba(212,160,84,0.15)',
    color: '#d4a054',
    padding: '0.2rem 0.6rem',
    borderRadius: '0 0 0.5rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  categoryTag: {
    alignSelf: 'flex-start',
    padding: '0.2rem 0.5rem',
    borderRadius: '0.375rem',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  cityName: {
    color: '#f1f5f9',
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: 0,
  },
  countryName: {
    color: '#64748b',
    fontSize: '0.8rem',
    margin: 0,
  },
  vibe: {
    color: '#94a3b8',
    fontSize: '0.8rem',
    margin: 0,
    lineHeight: 1.4,
    fontStyle: 'italic',
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '0.5rem',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  priceLabel: {
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  priceValue: {
    color: '#10b981',
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '0.75rem',
    cursor: 'pointer',
    padding: '0.25rem 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  detailSection: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
  },
  detailIcon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  detailKey: {
    color: '#64748b',
    fontSize: '0.7rem',
    margin: '0',
    textTransform: 'uppercase',
  },
  detailVal: {
    color: '#cbd5e1',
    fontSize: '0.8rem',
    margin: 0,
  },
  breakdownBox: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '0.375rem',
    padding: '0.5rem 0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  planBtn: {
    marginTop: 'auto',
    padding: '0.6rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    color: '#f1f5f9',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
};

export default TripComparisonCard;
